import { createAction, props } from '@ngrx/store';
import {
  AgentPremiumCalculationBody,
  AgentQuotationBody,
  CalculationAddOns,
  DiscountCodeResponse,
  PersonsWith,
  PremiumCalculationApi,
  PremiumCalculationBody,
  QuotationBody,
  QuotationResponse,
} from 'src/app/model/offer';
import { SendStaticAttachmentsResponse } from 'src/app/model/send-static-attachments.model';
import AppError from '../../model/app-error';
import { BlockValidatorResponse, KnfLimitCheckPayload } from '../../model/async-validation.model';
import { CalculationToClientConfirmPayload, CalculationToClientConfirmResponse } from '../../model/calculation-to-client-confirm.model';
import { ConclusionType } from '../../model/conclusion-type.model';
import { ConfirmPayload, ConfirmResponse } from '../../model/confirm.model';
import { ProcessType } from '../../model/process-type.model';
import { FormOfPayment, PurchaseAgentPayload, PurchaseAgentResponse } from '../../model/purchase-agent.model';
import {
  AgentSaveCalculationPayload,
  LoadPolicyDataResponse,
  SaveCalculationPayload,
  SaveCalculationResponse,
} from '../../model/save-calculation.model';

export enum ProcessActions {
  Reset = '[Process] Reset',
  NextStep = '[Process] Next step',
  PrevStep = '[Process] Prev step',
  GoToStep = '[Process] Go to step',
  SetStep = '[Process] Set step',
  SetKind = '[Process] Set kind',
  Success = '[Process] Success',
  SetConclusion = '[Process] Set conclusion',
  SaveCalculationStart = '[Process] Save calculation start',
  SaveCalculationSuccess = '[Process] Save calculation success',
  SaveCalculationError = '[Process] Save calculation error',
  SaveOfferCalculationStart = '[Process] Save offer calculation start',
  SaveOfferCalculationSuccess = '[Process] Save offer calculation success',
  SaveOfferCalculationError = '[Process] Save offer calculation error',
  SaveOfferCalculationReset = '[Process] Save offer calculation reset',
  ConfirmStart = '[Process] Confirm start',
  ConfirmSuccess = '[Process] Confirm success',
  ConfirmError = '[Process] Confirm error',
  SelectPackage = '[Process] Select package',
  SetOfferValue = '[Process] Set offer value',
  SetOfferValueBeforeDiscounts = '[Process] Set offer value before discounts',
  SetAddons = '[Process] Set addons',
  ConclusionStart = '[Process] Conclusion start',
  ConclusionSuccess = '[Process] Conclusion success',
  ConclusionError = '[Process] Conclusion error',
  ConclusionReset = '[Process] Conclusion reset',
  PushNavigationError = '[Process] Push navigation error',
  ResetNavigationError = '[Process] Reset navigation error',
  SetFormOfPayment = '[Process] Form of payment',
  SendStaticAttachmentsStart = '[Process] Send Static Attachments start',
  SendStaticAttachmentsSuccess = '[Process] Send Static Attachments success',
  SendStaticAttachmentsError = '[Process] Send Static Attachments error',
  SendStaticAttachmentsReset = '[Process] Send Static Attachments reset',
  calculationToClientConfirmStart = '[Process] Calculation To Client Confirm start',
  calculationToClientConfirmSuccess = '[Process] Calculation To Client Confirm success',
  calculationToClientConfirmError = '[Process] Calculation To Client Confirm error',
  FetchQuotationStart = '[Process] Fetch Quotation Start',
  FetchQuotationSuccess = '[Process] Fetch Quotation Success',
  FetchQuotationError = '[Process] Fetch Quotation Error',
  FetchDiscountCodeStart = '[Process] Fetch Discount Code Start',
  FetchDiscountCodeSuccess = '[Process] Fetch Discount Code Success',
  FetchDiscountCodeError = '[Process] Fetch Discount Code Error',
  RemoveDiscountCode = '[Process] Remove Discount Code',
  FetchPremiumCalculationStart = '[Process] Fetch Premium Calculation Start',
  FetchPremiumCalculationSuccess = '[Process] Fetch Premium Calculation Success',
  FetchPremiumCalculationError = '[Process] Fetch Premium Calculation Error',
  SetSelectedTab = '[Process] Set Selected Tab',
  SetCustomizeAlcoholIncluded = '[Process] Set Customize Alcohol Included',
  SetCustomizeForTheActiveIncluded = '[Process] Set Customize For The Active Included',
  SetAgentSectionPremiumPercentIncrease = '[Process] Set Agent Section Premium Percent Increase',
  SelectPackageAddons = '[Process] Select Package Addons',
  SetTravelDate = '[Process] Set travel date',
  UsePolicyDataStart = '[Process] Use policy data start',
  UsePolicyDataSuccess = '[Process] Use policy data success',
  UsePolicyDataError = '[Process] Use policy data error',
  UsePolicyDataReset = '[Process] Use policy data reset',
  SetSendBackOfficeData = '[Process] Set send backoffice data',
  SetIsOfferPriceBoxVisible = '[Process] Set offer price box visibility',
  KnfLimitStart = '[Process] KNF limit start',
  KnfLimitSuccess = '[Process] KNF limit success',
  KnfLimitError = '[Process] KNF limit error',
  blockListCheckStart = '[Process] block list check start',
  blockListCheckSuccess = '[Process] block list check success',
  blockListCheckError = '[Process] block list check error',
}

const reset = createAction(ProcessActions.Reset);
const nextStep = createAction(ProcessActions.NextStep);
const prevStep = createAction(ProcessActions.PrevStep);
const goToStep = createAction(ProcessActions.GoToStep, props<{ step: number }>());
const setStep = createAction(ProcessActions.SetStep, props<{ step: number }>());
const setKind = createAction(ProcessActions.SetKind, props<{ kind: ProcessType }>());
const setSuccess = createAction(ProcessActions.Success, props<{ success: boolean }>());
const setConclusion = createAction(ProcessActions.SetConclusion, props<{ conclusion: ConclusionType }>());
const saveCalculationStart = createAction(
  ProcessActions.SaveCalculationStart,
  props<{ payload: SaveCalculationPayload | AgentSaveCalculationPayload }>(),
);
const saveCalculationSuccess = createAction(ProcessActions.SaveCalculationSuccess, props<{ success: SaveCalculationResponse }>());
const saveCalculationError = createAction(ProcessActions.SaveCalculationError, props<{ error: AppError | null }>());
const saveOfferCalculationStart = createAction(ProcessActions.SaveOfferCalculationStart, props<{ payload: AgentSaveCalculationPayload }>());
const saveOfferCalculationSuccess = createAction(ProcessActions.SaveOfferCalculationSuccess, props<{ success: SaveCalculationResponse }>());
const saveOfferCalculationError = createAction(ProcessActions.SaveOfferCalculationError, props<{ error: AppError | null }>());
const saveOfferCalculationReset = createAction(ProcessActions.SaveOfferCalculationReset);
const confirmStart = createAction(ProcessActions.ConfirmStart, props<{ payload: ConfirmPayload }>());
const confirmSuccess = createAction(ProcessActions.ConfirmSuccess, props<{ success: ConfirmResponse }>());
const confirmError = createAction(ProcessActions.ConfirmError, props<{ error: ConfirmResponse }>());
const setOfferValue = createAction(ProcessActions.SetOfferValue, props<{ offerValue: number | null }>());
const setOfferValueBeforeDiscounts = createAction(
  ProcessActions.SetOfferValueBeforeDiscounts,
  props<{ offerValueBeforeDiscounts: number | null }>(),
);
const selectPackage = createAction(ProcessActions.SelectPackage, props<{ packageName: string }>());
const setAddons = createAction(ProcessActions.SetAddons, props<{ addOns: CalculationAddOns }>());
const conclusionStart = createAction(ProcessActions.ConclusionStart, props<{ payload: PurchaseAgentPayload }>());
const conclusionSuccess = createAction(ProcessActions.ConclusionSuccess, props<{ success: PurchaseAgentResponse }>());
const conclusionError = createAction(ProcessActions.ConclusionError, props<{ error: AppError | null }>());
const pushNavigationError = createAction(ProcessActions.PushNavigationError, props<{ error: string; preserve?: boolean }>());
const resetNavigationError = createAction(ProcessActions.ResetNavigationError);
const setFormOfPayment = createAction(ProcessActions.SetFormOfPayment, props<{ formOfPayment: FormOfPayment }>());
const sendStaticAttachmentsStart = createAction(ProcessActions.SendStaticAttachmentsStart, props<{ hashCode: string }>());
const sendStaticAttachmentsSuccess = createAction(
  ProcessActions.SendStaticAttachmentsSuccess,
  props<{ success: SendStaticAttachmentsResponse }>(),
);
const sendStaticAttachmentsError = createAction(ProcessActions.SendStaticAttachmentsError, props<{ error: AppError }>());
const sendStaticAttachmentsReset = createAction(ProcessActions.SendStaticAttachmentsReset);
const calculationToClientConfirmStart = createAction(
  ProcessActions.calculationToClientConfirmStart,
  props<{ payload: CalculationToClientConfirmPayload }>(),
);
const calculationToClientConfirmSuccess = createAction(
  ProcessActions.calculationToClientConfirmSuccess,
  props<{ success: CalculationToClientConfirmResponse }>(),
);
const calculationToClientConfirmError = createAction(ProcessActions.calculationToClientConfirmError, props<{ error: AppError | null }>());
const fetchQuotationStart = createAction(
  ProcessActions.FetchQuotationStart,
  props<{ body: QuotationBody | AgentQuotationBody; slim: boolean }>(),
);
const fetchQuotationSuccess = createAction(ProcessActions.FetchQuotationSuccess, props<{ data: QuotationResponse }>());
const fetchQuotationError = createAction(ProcessActions.FetchQuotationError, props<{ error: AppError }>());
const fetchDiscountCodeStart = createAction(ProcessActions.FetchDiscountCodeStart, props<{ discountCode: string }>());
const fetchDiscountCodeSuccess = createAction(ProcessActions.FetchDiscountCodeSuccess, props<{ data: DiscountCodeResponse }>());
const fetchDiscountCodeError = createAction(ProcessActions.FetchDiscountCodeError, props<{ error: AppError | null }>());
const removeDiscountCode = createAction(ProcessActions.RemoveDiscountCode);
const fetchPremiumCalculationStart = createAction(
  ProcessActions.FetchPremiumCalculationStart,
  props<{ body: PremiumCalculationBody | AgentPremiumCalculationBody }>(),
);
const fetchPremiumCalculationSuccess = createAction(
  ProcessActions.FetchPremiumCalculationSuccess,
  props<{ data: PremiumCalculationApi; packageName: string }>(),
);
const fetchPremiumCalculationError = createAction(
  ProcessActions.FetchPremiumCalculationError,
  props<{ packageName: string; error: AppError }>(),
);
const setSelectedTab = createAction(ProcessActions.SetSelectedTab, props<{ selectedTab: number }>());
const setCustomizeAlcoholIncluded = createAction(
  ProcessActions.SetCustomizeAlcoholIncluded,
  props<{ customizeAlcoholIncluded: PersonsWith[] }>(),
);
const setCustomizeForTheActiveIncluded = createAction(
  ProcessActions.SetCustomizeForTheActiveIncluded,
  props<{ customizeForTheActiveIncluded: PersonsWith[] }>(),
);
const setAgentSectionPremiumPercentIncrease = createAction(
  ProcessActions.SetAgentSectionPremiumPercentIncrease,
  props<{ premiumPercentIncrease: number }>(),
);
const selectPackageAddons = createAction(ProcessActions.SelectPackageAddons, props<{ addon: string; value: boolean }>());
const usePolicyDataStart = createAction(ProcessActions.UsePolicyDataStart, props<{ policyNumber: string | null; leadId: string | null }>());
const usePolicyDataSuccess = createAction(ProcessActions.UsePolicyDataSuccess, props<{ success: LoadPolicyDataResponse }>());
const usePolicyDataError = createAction(ProcessActions.UsePolicyDataError, props<{ error: AppError | null }>());
const usePolicyDataReset = createAction(ProcessActions.UsePolicyDataReset);
const setSendBackOfficeData = createAction(ProcessActions.SetSendBackOfficeData, props<{ sendBackOfficeData: boolean }>());
const setIsOfferPriceBoxVisible = createAction(ProcessActions.SetIsOfferPriceBoxVisible, props<{ isOfferPriceBoxVisible: boolean }>());
const knfLimitStart = createAction(ProcessActions.KnfLimitStart, props<{ payload: KnfLimitCheckPayload }>());
const knfLimitSuccess = createAction(ProcessActions.KnfLimitSuccess, props<{ success: BlockValidatorResponse }>());
const knfLimitError = createAction(ProcessActions.KnfLimitError, props<{ error: AppError | null }>());
const blockListCheckStart = createAction(ProcessActions.blockListCheckStart);
const blockListCheckSuccess = createAction(ProcessActions.blockListCheckSuccess, props<{ success: BlockValidatorResponse }>());
const blockListCheckError = createAction(ProcessActions.blockListCheckError, props<{ error: AppError | null }>());

const processActions = {
  reset,
  nextStep,
  prevStep,
  goToStep,
  setStep,
  setKind,
  setSuccess,
  setConclusion,
  saveCalculationStart,
  saveCalculationSuccess,
  saveCalculationError,
  saveOfferCalculationStart,
  saveOfferCalculationSuccess,
  saveOfferCalculationError,
  saveOfferCalculationReset,
  confirmStart,
  confirmSuccess,
  confirmError,
  setOfferValue,
  setOfferValueBeforeDiscounts,
  selectPackage,
  setAddons,
  conclusionStart,
  conclusionSuccess,
  conclusionError,
  pushNavigationError,
  resetNavigationError,
  setFormOfPayment,
  sendStaticAttachmentsStart,
  sendStaticAttachmentsSuccess,
  sendStaticAttachmentsError,
  sendStaticAttachmentsReset,
  calculationToClientConfirmStart,
  calculationToClientConfirmSuccess,
  calculationToClientConfirmError,
  fetchQuotationStart,
  fetchQuotationSuccess,
  fetchQuotationError,
  fetchDiscountCodeStart,
  fetchDiscountCodeSuccess,
  fetchDiscountCodeError,
  removeDiscountCode,
  fetchPremiumCalculationStart,
  fetchPremiumCalculationSuccess,
  fetchPremiumCalculationError,
  setSelectedTab,
  setCustomizeAlcoholIncluded,
  setCustomizeForTheActiveIncluded,
  setAgentSectionPremiumPercentIncrease,
  selectPackageAddons,
  usePolicyDataStart,
  usePolicyDataSuccess,
  usePolicyDataError,
  usePolicyDataReset,
  setSendBackOfficeData,
  setIsOfferPriceBoxVisible,
  knfLimitStart,
  knfLimitSuccess,
  knfLimitError,
  blockListCheckStart,
  blockListCheckSuccess,
  blockListCheckError,
};

export default processActions;
